import {
  ESdChangeReason,
  IKuikaStyle,
  InvisibleStylePart,
  ITransitionItem,
  TransitionTimingFunction,
  Visibility
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import PopoverInput from "./popover-input/popover-input";
import {
  applyTransitionPreset,
  summarizeTransition,
  TRANSITION_EASING_OPTIONS,
  TRANSITION_PRESET_OPTIONS,
  TRANSITION_PROPERTY_GROUPS
} from "./styling-effects-helpers";
import { styleSectionNeedsRender } from "./styling-window-helpers";

const DEFAULT_DURATION: number = 200;
const DURATION_MIN: number = 0;
const DURATION_MAX: number = 5000;
const DELAY_MIN: number = 0;
const DELAY_MAX: number = 5000;

const createTransition = (): ITransitionItem => ({
  id: uuidv4(),
  property: "opacity",
  duration: DEFAULT_DURATION,
  timingFunction: "ease",
  delay: 0
});

interface TransitionPopoverContentProps {
  item: ITransitionItem;
  onPatch: (patch: Partial<ITransitionItem>) => void;
  onApplyPreset: (presetName: string) => void;
}

const TransitionPopoverContent: React.FC<TransitionPopoverContentProps> = ({ item, onPatch, onApplyPreset }) => {
  return (
    <div className="effects-transition__popover">
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Property</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          groups={TRANSITION_PROPERTY_GROUPS}
          value={item.property}
          onChange={(next: string | number | (string | number)[] | null | any): void => {
            if (typeof next === "string") onPatch({ property: next });
          }}
          searchable
          dataTestId="effects-transition-property"
        />
      </div>
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Preset</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          options={TRANSITION_PRESET_OPTIONS}
          value={undefined}
          placeholder="Pick a preset"
          onChange={(next: string | number | (string | number)[] | null | any): void => {
            if (typeof next === "string") onApplyPreset(next);
          }}
          dataTestId="effects-transition-preset"
        />
      </div>
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Duration</KuikaSmallText>
        <KuikaSlider
          value={item.duration ?? DEFAULT_DURATION}
          onChange={(next: number): void => onPatch({ duration: next })}
          min={DURATION_MIN}
          max={DURATION_MAX}
          step={10}
          precision={0}
          unit="ms"
          size="sm"
          dataTestId="effects-transition-duration"
        />
      </div>
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Easing</KuikaSmallText>
        <KuikaSelectbox
          size="sm"
          options={TRANSITION_EASING_OPTIONS}
          value={item.timingFunction === "cubic-bezier" ? undefined : (item.timingFunction ?? "ease")}
          placeholder={item.timingFunction === "cubic-bezier" ? "Spring (preset)" : "Ease"}
          onChange={(next: string | number | (string | number)[] | null | any): void => {
            if (typeof next === "string") {
              // Switching to a non-bezier preset clears any preset-injected curve.
              onPatch({ timingFunction: next as TransitionTimingFunction, cubicBezier: undefined });
            }
          }}
          dataTestId="effects-transition-easing"
        />
      </div>
      <div className="effects-transition__row">
        <KuikaSmallText style={{ color: "var(--dark-2)" }}>Delay</KuikaSmallText>
        <KuikaInput
          type="number"
          size="sm"
          value={item.delay ?? 0}
          rightUnit="ms"
          min={DELAY_MIN}
          max={DELAY_MAX}
          onChange={(_e, next: string | number): void => {
            const num: number = typeof next === "number" ? next : Number(next);
            if (!Number.isNaN(num)) onPatch({ delay: num });
          }}
          data-testid="effects-transition-delay"
        />
      </div>
    </div>
  );
};

export interface IEffectsTransitionsComponentProps {
  form: FormInstance<IKuikaStyle>;
}

interface IEffectsTransitionsComponentState {
  placeholder: never[];
}

class EffectsTransitionsComponent extends PureSdComponent<
  IEffectsTransitionsComponentProps & IWithSDProps,
  IEffectsTransitionsComponentState
> {
  constructor(props: IEffectsTransitionsComponentProps & IWithSDProps) {
    super(props);
    this.state = { placeholder: [] };
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => styleSectionNeedsRender(changeReason);

  private isEffectsHidden = (): boolean =>
    this.props.sd.getStylesVisibility(InvisibleStylePart.Effects) === Visibility.Hidden;

  private ensureEffectsSection = (fieldsValue: IKuikaStyle): void => {
    const kuikaid: string | undefined = this.props.sd.selectedKuikaIds[0];
    if (!fieldsValue.effects) {
      fieldsValue.effects = { effectsId: null, kuikaid: kuikaid ?? "" };
    }
  };

  private submitForm = (fieldsValue: IKuikaStyle): void => {
    // Do NOT call form.resetFields() here — it unmounts every Form.Item
    // subtree, which resets the local `useState` open-state inside each
    // PopoverInput rendered under shouldUpdate. Popovers close mid-edit
    // as a result. setFieldsValue alone replaces the transitions array;
    // the explicit submit() triggers persistence.
    this.props.form.setFieldsValue(fieldsValue);
    this.props.form.submit();
  };

  private commitTransitions = (next: ITransitionItem[]): void => {
    const fieldsValue: IKuikaStyle = this.props.form.getFieldsValue();
    this.ensureEffectsSection(fieldsValue);
    if (fieldsValue.effects) {
      fieldsValue.effects.transitions = next;
    }
    this.submitForm(fieldsValue);
  };

  private patchTransitionAt = (index: number, patch: Partial<ITransitionItem>): void => {
    const current: ITransitionItem[] =
      (this.props.form.getFieldValue(["effects", "transitions"]) as ITransitionItem[]) || [];
    const target: ITransitionItem | undefined = current[index];
    if (!target) return;
    const next: ITransitionItem[] = current.map((it: ITransitionItem, i: number): ITransitionItem =>
      i === index ? { ...target, ...patch } : it
    );
    this.commitTransitions(next);
  };

  private applyPresetAt = (index: number, presetName: string): void => {
    const current: ITransitionItem[] =
      (this.props.form.getFieldValue(["effects", "transitions"]) as ITransitionItem[]) || [];
    const target: ITransitionItem | undefined = current[index];
    if (!target) return;
    const next: ITransitionItem[] = current.map((it: ITransitionItem, i: number): ITransitionItem =>
      i === index ? applyTransitionPreset(target, presetName) : it
    );
    this.commitTransitions(next);
  };

  private removeTransitionAt = (index: number): void => {
    const current: ITransitionItem[] =
      (this.props.form.getFieldValue(["effects", "transitions"]) as ITransitionItem[]) || [];
    this.commitTransitions(current.filter((_: ITransitionItem, i: number): boolean => i !== index));
  };

  private addTransition = (): void => {
    const current: ITransitionItem[] =
      (this.props.form.getFieldValue(["effects", "transitions"]) as ITransitionItem[]) || [];
    this.commitTransitions([...current, createTransition()]);
  };

  render(): React.ReactNode {
    if (this.isEffectsHidden()) {
      return null;
    }

    return (
      <div className="effects-section__transitions">
        <div className="effects-section__multi-header">
          <KuikaSmallText style={{ color: "var(--dark-7)", fontWeight: 500 }}>Transitions</KuikaSmallText>
          <div className="effects-section__multi-header-actions">
            <button
              type="button"
              aria-label="Add transition"
              className="effects-section__icon-button"
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                this.addTransition();
              }}
              data-testid="effects-transition-add"
            >
              <KuikaSvg name="plus" width={14} height={14} />
            </button>
          </div>
        </div>
        <Form.Item noStyle shouldUpdate>
          {() => {
            const items: ITransitionItem[] =
              (this.props.form.getFieldValue(["effects", "transitions"]) as ITransitionItem[]) || [];
            if (items.length === 0) {
              return null;
            }
            return (
              <div className="effects-section__multi-body">
                {items.map((item: ITransitionItem, index: number) => (
                  <PopoverInput
                    key={item.id}
                    value={summarizeTransition(item)}
                    placeholder="Configure transition"
                    onClear={(): void => this.removeTransitionAt(index)}
                    popoverContent={
                      <TransitionPopoverContent
                        item={item}
                        onPatch={(patch: Partial<ITransitionItem>): void => this.patchTransitionAt(index, patch)}
                        onApplyPreset={(presetName: string): void => this.applyPresetAt(index, presetName)}
                      />
                    }
                    popoverTitle="Transition"
                    popoverPosition="left"
                    popoverAlign="end"
                    dataTestId={`effects-transition-${index}`}
                  />
                ))}
              </div>
            );
          }}
        </Form.Item>
      </div>
    );
  }
}

export default withSD<IEffectsTransitionsComponentProps>(EffectsTransitionsComponent);
